/**
 * Visualizer Manifest — Single Source of Truth
 *
 * Every piece of visualizer metadata lives here: tool IDs, modes, labels,
 * icons, compatible types, editability, pane constraints, header actions,
 * and semantic-type → default-mode mappings.
 *
 * All other modules derive their maps/sets/helpers from this manifest.
 * This eliminates the drift that previously existed across
 * VisualizerModeRegistry, visualizerToolMapping, visualizerModeRegistrations,
 * usePipelineStore, useAddVisualizerStep, and EditorPanel.
 *
 * IMPORTANT: This module must remain a leaf — no ToolRegistry or runtime
 * imports. It is used during Zustand persist migrations (module-load time).
 */

import type { ISemanticType } from '@/interfaces/ISemanticType';
import type { VisualizerMode } from '@/store/useVisualizerStore';

// ─────────────────────────────────────────────────────────────────────────────
// Manifest entry type
// ─────────────────────────────────────────────────────────────────────────────

export interface VisualizerManifestEntry {
  /** Tool ID in the ToolRegistry (e.g. 'json-tree-visualizer'). */
  readonly toolId: string;
  /** Mode identifier matching the VisualizerMode union. */
  readonly mode: VisualizerMode;
  /** Human-readable label for mode selectors. */
  readonly label: string;
  /** Icon emoji/string for mode selectors. */
  readonly icon: string;
  /** Semantic types this mode can render, or 'all' for universal modes. */
  readonly compatibleTypes: readonly string[] | 'all';
  /** Whether this mode supports inline editing. */
  readonly editable: boolean;
  /** Which panes this mode can appear in. Undefined means both. */
  readonly panes?: readonly ('input' | 'output')[];
  /** Actions this mode exposes in the panel header. */
  readonly headerActions?: readonly string[];
}

// ─────────────────────────────────────────────────────────────────────────────
// The manifest — 9 visualizer entries
// ─────────────────────────────────────────────────────────────────────────────

export const VISUALIZER_MANIFEST: readonly VisualizerManifestEntry[] = [
  {
    toolId: 'editor-visualizer',
    mode: 'raw',
    label: 'Raw Editor',
    icon: '✎',
    compatibleTypes: 'all',
    editable: true,
  },
  {
    toolId: 'json-tree-visualizer',
    mode: 'tree',
    label: 'Tree View',
    icon: '🌲',
    compatibleTypes: ['JSON', 'YAML', 'TOML'],
    editable: true,
    headerActions: ['expandAll', 'collapseAll'],
  },
  {
    toolId: 'tabular-data-visualizer',
    mode: 'table',
    label: 'Table View',
    icon: '#',
    compatibleTypes: ['CSV', 'TSV', 'Pipe'],
    editable: true,
  },
  {
    toolId: 'structured-table-visualizer',
    mode: 'structured-table',
    label: 'Structured Table',
    icon: '⬜',
    compatibleTypes: ['JSON', 'YAML', 'TOML'],
    editable: false,
  },
  {
    toolId: 'diff-visualizer',
    mode: 'diff',
    label: 'Diff View',
    icon: '±',
    compatibleTypes: 'all',
    editable: false,
    panes: ['output'],
  },
  {
    toolId: 'json-diagram-visualizer',
    mode: 'diagram',
    label: 'Diagram',
    icon: '◇',
    compatibleTypes: ['JSON', 'YAML', 'XML'],
    editable: true,
  },
  {
    toolId: 'markdown-preview-visualizer',
    mode: 'markdown',
    label: 'Markdown',
    icon: 'M',
    compatibleTypes: ['Markdown', 'Text'],
    editable: true,
  },
  {
    toolId: 'regex-railroad-visualizer',
    mode: 'railroad',
    label: 'Railroad',
    icon: '→',
    compatibleTypes: ['Regex', 'Text'],
    editable: true,
  },
  {
    toolId: 'hex-visualizer',
    mode: 'hex',
    label: 'Hex View',
    icon: '0x',
    compatibleTypes: 'all',
    editable: true,
  },
] as const;

// ─────────────────────────────────────────────────────────────────────────────
// Semantic type → default mode mapping
// ─────────────────────────────────────────────────────────────────────────────

const SEMANTIC_TYPE_DEFAULT_MODES: Record<string, VisualizerMode> = {
  'JSON': 'tree',
  'XML': 'diagram',
  'YAML': 'tree',
  'TOML': 'tree',
  'CSV': 'table',
  'TSV': 'table',
  'Pipe': 'table',
  'HTML': 'raw',
  'Markdown': 'markdown',
  'Regex': 'railroad',
  'Text': 'raw',
  'Base64': 'raw',
  'URL': 'raw',
  'URLEncoded': 'raw',
  'Hex': 'hex',
  'JWT': 'raw',
  'UUID': 'raw',
  'Hash': 'raw',
  'SVG': 'raw',
  'Timestamp': 'raw',
};

// ─────────────────────────────────────────────────────────────────────────────
// Derived lookup maps (built once at module load)
// ─────────────────────────────────────────────────────────────────────────────

const _modeToEntry = new Map<VisualizerMode, VisualizerManifestEntry>();
const _toolIdToEntry = new Map<string, VisualizerManifestEntry>();

for (const entry of VISUALIZER_MANIFEST) {
  _modeToEntry.set(entry.mode, entry);
  _toolIdToEntry.set(entry.toolId, entry);
}

// ─────────────────────────────────────────────────────────────────────────────
// Public API — tool ID ↔ mode mapping
// ─────────────────────────────────────────────────────────────────────────────

/** Get the tool ID for a visualizer mode. Returns undefined for unknown modes. */
export function getToolIdForMode(mode: VisualizerMode): string | undefined {
  return _modeToEntry.get(mode)?.toolId;
}

/** Get the visualizer mode for a tool ID. Returns undefined for non-visualizer tools. */
export function getModeForToolId(toolId: string): VisualizerMode | undefined {
  return _toolIdToEntry.get(toolId)?.mode;
}

/** Get the visualizer mode for a tool ID, falling back to 'raw'. */
export function getModeForToolIdOrDefault(toolId: string): VisualizerMode {
  return _toolIdToEntry.get(toolId)?.mode ?? 'raw';
}

/** Check whether a tool ID is a known visualizer tool. */
export function isVisualizerToolId(toolId: string): boolean {
  return _toolIdToEntry.has(toolId);
}

// ─────────────────────────────────────────────────────────────────────────────
// Public API — compatible types & editability
// ─────────────────────────────────────────────────────────────────────────────

/** Get the compatible semantic types for a mode. Returns 'all' for universal modes. */
export function getCompatibleTypes(mode: VisualizerMode): readonly string[] | 'all' {
  return _modeToEntry.get(mode)?.compatibleTypes ?? 'all';
}

/** Check whether a mode supports inline editing. */
export function isEditable(mode: VisualizerMode): boolean {
  return _modeToEntry.get(mode)?.editable ?? false;
}

/** Get the manifest entry for a mode (label, icon, etc.). */
export function getManifestEntry(mode: VisualizerMode): VisualizerManifestEntry | undefined {
  return _modeToEntry.get(mode);
}

// ─────────────────────────────────────────────────────────────────────────────
// Public API — migration visualizer IDs
// ─────────────────────────────────────────────────────────────────────────────

let _migrationIds: Set<string> | null = null;

/** Get the set of all visualizer tool IDs (safe to call at module-load time). */
export function getMigrationVisualizerIds(): Set<string> {
  if (!_migrationIds) {
    _migrationIds = new Set(VISUALIZER_MANIFEST.map((e) => e.toolId));
  }
  return _migrationIds;
}

// ─────────────────────────────────────────────────────────────────────────────
// Public API — semantic type → default mode
// ─────────────────────────────────────────────────────────────────────────────

/** Get the default visualizer mode for a semantic type. Returns 'raw' if unmapped. */
export function getDefaultModeForType(
  semanticType: ISemanticType | null | undefined,
): VisualizerMode {
  if (!semanticType) return 'raw';
  return SEMANTIC_TYPE_DEFAULT_MODES[semanticType.semanticType] ?? 'raw';
}

// ─────────────────────────────────────────────────────────────────────────────
// Derived record for consumers that need the old VISUALIZER_TOOL_TO_MODE shape
// ─────────────────────────────────────────────────────────────────────────────

/** Record<toolId, mode> for all visualizer tools — drop-in replacement for VISUALIZER_TOOL_TO_MODE. */
export const TOOL_TO_MODE: Record<string, VisualizerMode> = Object.fromEntries(
  VISUALIZER_MANIFEST.map((e) => [e.toolId, e.mode]),
);
